import React from 'react';
import styles from './app-icon.module.css';
import { shake } from 'radash';
import { clsx } from '@/lib/utils.ts';
import {
    Barcode,
    Bookmark,
    BookmarkCheck,
    DraftingCompass,
    Building2,
    Cog,
    Bubbles,
    Menu,
    Search,
    LayoutDashboard,
    Zap,
    Flame,
    LogOut,
    Camera,
    Headset,
    List,
    RefreshCcw,
    Lock,
    ShieldUser,
    Info,
    ArrowLeft,
    Plus,
    X,
    Pencil,
    ArrowDownUp,
    BanknoteX,
    Ellipsis,
    RectangleEllipsis,
    Calendar1,
    CalendarClock,
    Image,
    Gauge,
    MapPin,
    ArrowUpDown,
    Users,
    User,
    Lamp,
    Import,
    House,
    Trash,
} from 'lucide-react';

interface AppIconProps {
    icon: string;
    size?: number;
    color?: string;
    className?: string;
    onClick?: React.MouseEventHandler;
}

export const AppIcon: React.FC<AppIconProps> = ({
    icon,
    size,
    color,
    className = 'w-4 h-4',
    onClick,
}) => {
    const iconProps = {
        color,
        size,
        className,
        onClick,
    };

    switch (icon) {
        case 'home':
            return <LayoutDashboard {...iconProps} />;
        case 'house':
            return <Building2 {...iconProps} />;
        case 'settings':
            return <Cog {...iconProps} />;
        case 'search':
            return <Search {...iconProps} />;
        case 'options':
            return <Menu {...iconProps} />;
        case 'compass':
            return <DraftingCompass {...iconProps} />;
        case 'bookmarkChecked':
            return <BookmarkCheck {...iconProps} />;
        case 'bookmark':
            return <Bookmark {...iconProps} />;
        case 'barcode':
            return <Barcode {...iconProps} />;
        case 'waves':
            return <Bubbles {...iconProps} />;
        case 'zap':
            return <Zap {...iconProps} />;
        case 'flame':
            return <Flame {...iconProps} />;
        case 'log-out':
            return <LogOut {...iconProps} />;
        case 'camera':
            return <Camera {...iconProps} />;
        case 'headset':
            return <Headset {...iconProps} />;
        case 'list':
            return <List {...iconProps} />;
        case 'refresh-ccw':
            return <RefreshCcw {...iconProps} />;
        case 'user-lock':
            return <Lock {...iconProps} />;
        case 'shield-user':
            return <ShieldUser {...iconProps} />;
        case 'info':
            return <Info {...iconProps} />;
        case 'arrow-left':
            return <ArrowLeft {...iconProps} />;
        case 'plus':
            return <Plus {...iconProps} />;
        case 'x':
            return <X {...iconProps} />;
        case 'pencil':
            return <Pencil {...iconProps} />;
        case 'switch':
            return <ArrowDownUp {...iconProps} />;
        case 'banknote-x':
            return <BanknoteX {...iconProps} />;
        case 'ellipsis':
            return <Ellipsis {...iconProps} />;
        case 'rectangle-ellipsis':
            return <RectangleEllipsis {...iconProps} />;
        case 'calendar':
            return <Calendar1 {...iconProps} />;
        case 'calendar-clock':
            return <CalendarClock {...iconProps} />;
        case 'image':
            return <Image {...iconProps} />;
        case 'gauge':
            return <Gauge {...iconProps} />;
        case 'map-pin':
            return <MapPin {...iconProps} />;
        case 'account-switch':
            return <ArrowUpDown {...iconProps} />;
        case 'user':
            return <User {...iconProps} />;
        case 'user-group':
            return <Users {...iconProps} />;
        case 'building-2':
            return <Building2 {...iconProps} />;
        case 'lamp':
            return <Lamp {...iconProps} />;
        case 'import':
            return <Import {...iconProps} />;
        case 'energy-provider':
            return <DraftingCompass {...iconProps} />;
        case 'house-manager':
            return <House {...iconProps} />;
        case 'delete':
            return <Trash {...iconProps} />;
    }

    return (
        <div
            className={clsx(styles.appIcon, 'w-4 h-4', className)}
            style={shake({ fontSize: size, color })}
            onClick={onClick}>
            {icon}
        </div>
    );
};

export const appIcons = {
    add: 'plus',
    accountSwitch: 'account-switch',
    back: 'arrow-left',
    camera: 'camera',
    cameraActive: '',
    calendar: 'calendar',
    calendarAlert: 'calendar-clock',
    close: 'x',
    counter: 'gauge',
    compass: 'compass',
    deleteProfile: '',
    deregister: 'banknote-x',
    edit: 'pencil',
    filter: 'options',
    filterActive: '',
    fire: 'flame',
    home: 'house',
    house: '',
    image: 'image',
    lamp: 'lamp',
    lampOff: 'lamp-off',
    location: 'map-pin',
    lightning: 'zap',
    list: 'list',
    lock: 'user-lock',
    logout: 'log-out',
    meter: 'rectangle-ellipsis',
    options: 'ellipsis',
    passwordReset: '',
    privacy: 'shield-user',
    refresh: 'refresh-ccw',
    scanIcon: 'barcode',
    search: 'search',
    settings: 'settings',
    starFilled: 'bookmarkChecked',
    starOutlined: 'bookmark',
    support: 'headset',
    swap: 'switch',
    water: 'waves',
    warn: 'warn',
    info: 'info',
    addBuilding: 'building-2',
    addUser: 'plus',
    accountDelete: 'banknote-x',
    community: 'community',
    delete: 'delete',
    energyProvider: 'energy-provider',
    export: 'export',
    houseManager: 'house-manager',
    import: 'import',
    user: 'user',
    users: 'user-group',
};
